<script setup>
import { computed, ref, onBeforeMount } from 'vue'
import { useRoute } from 'vue-router'
import { useOrgStore } from '@/stores/org'
import { API, HOST } from '@/utils/http'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Plus, Trash2 } from 'lucide-vue-next'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'

const route = useRoute()
const orgStore = useOrgStore()
const projectId = computed(() => route.params.project_id)
const agentsUrl = computed(
  () => `${HOST}/org/${orgStore.selectedOrg}/project-agents/${projectId.value}`
)

const projectAgents = ref([])
const library = ref([])

const confirmRemoveDialog = ref(null)
const pendingRemove = ref(null)

const fetchAgents = async () => {
  try {
    const [added, lib] = await Promise.all([
      API({ method: 'GET', url: agentsUrl.value }),
      API({ method: 'GET', url: `${agentsUrl.value}/library` }),
    ])
    projectAgents.value = added.data
    library.value = lib.data
  } catch (e) {
    toast({ title: 'Failed to load agents', variant: 'destructive' })
  }
}

const addAgent = async (agent) => {
  try {
    await API({
      method: 'PUT',
      url: agentsUrl.value,
      data: { agent_id: agent.id, enabled: true },
    })
    toast({ title: `Added ${agent.name}` })
    await fetchAgents()
  } catch (e) {
    toast({ title: 'Failed to add agent', variant: 'destructive' })
  }
}

const promptRemove = (pa) => {
  pendingRemove.value = pa
  confirmRemoveDialog.value?.open()
}

const removeAgent = async () => {
  if (!pendingRemove.value) return
  try {
    await API({
      method: 'DELETE',
      url: `${agentsUrl.value}/${pendingRemove.value.agent_id}`,
    })
    toast({ title: 'Agent removed' })
    await fetchAgents()
  } catch (e) {
    toast({ title: 'Failed to remove agent', variant: 'destructive' })
  } finally {
    pendingRemove.value = null
  }
}

onBeforeMount(fetchAgents)
</script>

<template>
  <div class="max-w-2xl space-y-6">
    <div>
      <div class="mb-1 flex items-center">
        <h3 class="text-base font-bold">Agents in this project ({{ projectAgents.length }})</h3>
      </div>
      <p class="mb-3 text-sm text-muted-foreground">
        Members can @-mention these agents in the project chat to ask them something.
      </p>

      <div v-if="projectAgents.length" class="space-y-2">
        <div
          v-for="pa in projectAgents"
          :key="pa.id"
          class="flex items-center gap-3 rounded-md border p-3"
        >
          <span class="text-lg">{{ pa.agent.emoji || '🤖' }}</span>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium">{{ pa.agent.name }}</div>
            <div class="text-xs text-muted-foreground truncate">{{ pa.agent.description }}</div>
          </div>
          <code class="text-xs text-muted-foreground">@{{ pa.agent.key }}</code>
          <Button
            variant="ghost"
            size="icon"
            class="h-8 w-8"
            title="Remove agent"
            @click="promptRemove(pa)"
          >
            <Trash2 class="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>
      <div v-else class="text-center py-6 text-muted-foreground text-sm">
        No agents added yet. Add one from the library below.
      </div>
    </div>

    <div>
      <div class="mb-3 flex items-center">
        <h3 class="text-base font-bold">Agent Library ({{ library.length }})</h3>
      </div>

      <div v-if="library.length" class="space-y-2">
        <div
          v-for="agent in library"
          :key="agent.id"
          class="flex items-center gap-3 rounded-md border p-3"
        >
          <span class="text-lg">{{ agent.emoji || '🤖' }}</span>
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium">{{ agent.name }}</div>
            <div class="text-xs text-muted-foreground truncate">{{ agent.description }}</div>
          </div>
          <Button type="button" variant="outline" size="sm" @click="addAgent(agent)">
            <Plus class="h-3.5 w-3.5 mr-1" />
            Add
          </Button>
        </div>
      </div>
      <div v-else class="text-center py-6 text-muted-foreground text-sm">
        All available agents are already added.
      </div>
    </div>

    <DialogConfirm
      ref="confirmRemoveDialog"
      title="Remove agent?"
      description="This removes the agent from the project and returns it to the library. Members will no longer be able to mention it in chat."
      confirm-text="Remove"
      variant="error"
      @confirm="removeAgent"
    />
  </div>
</template>
